import { getApiUrl } from "@/lib/api";
import { useState, useRef } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${getApiUrl()}/api`;

/**
 * ImageUpload Component
 * Uploads images to Cloudinary and returns the URL
 * 
 * @param {string} folder - Cloudinary folder path (e.g., "gamepop/games", "gamepop/achievements")
 * @param {string} currentImageUrl - Current image URL to display
 * @param {function} onImageUploaded - Callback when image is uploaded, receives the new URL
 * @param {string} className - Additional CSS classes
 * @param {string} size - Size variant: "sm" (64px), "md" (96px), "lg" (128px)
 */
export default function ImageUpload({ 
  folder = "gamepop/games",
  currentImageUrl = "",
  onImageUploaded,
  className = "",
  size = "md"
}) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl);
  const fileInputRef = useRef(null);

  const sizeClasses = {
    sm: "w-16 h-16",
    md: "w-24 h-24",
    lg: "w-32 h-32"
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }

    setUploading(true);

    try {
      // Step 1: Get signed upload params from backend
      const signatureRes = await fetch(`${API}/cloudinary/signature?folder=${encodeURIComponent(folder)}`);
      if (!signatureRes.ok) {
        throw new Error("Failed to get upload signature");
      }
      const sig = await signatureRes.json();

      // Step 2: Upload directly to Cloudinary
      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sig.api_key);
      formData.append("timestamp", sig.timestamp);
      formData.append("signature", sig.signature);
      formData.append("folder", sig.folder);
      formData.append("transformation", sig.transformation);

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: "POST", body: formData }
      );

      if (!uploadRes.ok) {
        let errorMessage = "Upload failed";
        try {
          const errorText = await uploadRes.text();
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error?.message || errorMessage;
        } catch (e) {
          // Response might not be JSON
          errorMessage = `Upload failed with status ${uploadRes.status}`;
        }
        throw new Error(errorMessage);
      }

      const uploadData = await uploadRes.json();
      
      // Get the secure URL
      const imageUrl = uploadData.secure_url;
      
      setPreviewUrl(imageUrl);
      onImageUploaded?.(imageUrl);
      toast.success("Image uploaded successfully");

    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload image");
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveImage = () => {
    setPreviewUrl("");
    onImageUploaded?.("");
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="image-upload-input"
      />
      
      <div 
        className={`
          ${sizeClasses[size]} 
          rounded-lg border-2 border-dashed border-white/20 
          bg-white/5 
          flex items-center justify-center 
          cursor-pointer 
          hover:border-white/40 hover:bg-white/10 
          transition-colors
          overflow-hidden
          relative
        `}
        onClick={handleClick}
        data-testid="image-upload-dropzone"
      >
        {uploading ? (
          <Loader2 className="w-6 h-6 animate-spin text-white/60" />
        ) : previewUrl ? (
          <>
            <img 
              src={previewUrl} 
              alt="Preview" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-full h-full items-center justify-center hidden">
              <ImageIcon className="w-6 h-6 text-white/40" />
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1 text-white/40">
            <Upload className="w-5 h-5" />
            <span className="text-[10px]">Upload</span>
          </div>
        )}
      </div>

      {/* Remove button */}
      {previewUrl && !uploading && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveImage();
          }}
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors"
          data-testid="image-upload-remove-btn"
        >
          <X className="w-3 h-3 text-white" />
        </button>
      )}
    </div>
  );
}

/**
 * Compact inline image upload for forms
 * Shows as a button that opens file picker
 */
export function ImageUploadButton({ 
  folder = "gamepop/games",
  currentImageUrl = "",
  onImageUploaded,
  className = ""
}) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }

    setUploading(true);

    try {
      const signatureRes = await fetch(`${API}/cloudinary/signature?folder=${encodeURIComponent(folder)}`);
      if (!signatureRes.ok) throw new Error("Failed to get upload signature");
      const sig = await signatureRes.json();

      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sig.api_key);
      formData.append("timestamp", sig.timestamp);
      formData.append("signature", sig.signature);
      formData.append("folder", sig.folder);
      formData.append("transformation", sig.transformation);

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: "POST", body: formData }
      );

      if (!uploadRes.ok) {
        const errorData = await uploadRes.json();
        throw new Error(errorData.error?.message || "Upload failed");
      }

      const uploadData = await uploadRes.json();
      onImageUploaded?.(uploadData.secure_url);
      toast.success("Image uploaded");

    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className={`border-white/20 hover:bg-white/10 ${className}`}
        data-testid="image-upload-btn"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
        ) : (
          <Upload className="w-4 h-4 mr-2" />
        )}
        {currentImageUrl ? "Replace Image" : "Upload Image"}
      </Button>
    </>
  );
}
